import { create } from 'zustand';
import { authApi } from '@/api/auth';
import type {
  User,
  LoginRequest,
  RegisterRequest,
  GoogleAuthRequest,
  AuthResponse,
} from '@/types/auth';
import type { Role, MemberScopes } from '@/types/rbac';

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  /** Epoch (ms) em que o access token expira. null quando não autenticado. */
  accessTokenExpiresAt: number | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;

  // RBAC fields
  accountId: string | null;
  memberId: string | null;
  role: Role | null;
  scopes: MemberScopes;
  globalAccess: boolean;
  
  // Actions
  login: (credentials: LoginRequest) => Promise<void>;
  register: (data: RegisterRequest) => Promise<void>;
  loginWithGoogle: (data: GoogleAuthRequest) => Promise<void>;
  logout: () => Promise<void>;
  refreshAccessToken: () => Promise<void>;
  refreshUserData: () => Promise<void>;
  refreshUserDataFresh: () => Promise<any>;
  handleAuthSuccessFromResponse: (response: AuthResponse) => void;
  clearError: () => void;
  initializeAuth: () => void;
  /**
   * Indica se o access token está expirado ou prestes a expirar (dentro da margem).
   * Usado pelo interceptor de request para renovar o token proativamente,
   * evitando 401 desnecessários no backend.
   */
  isAccessTokenExpiring: () => boolean;
}

const TOKEN_KEY = 'feedbatch_access_token';
const REFRESH_TOKEN_KEY = 'feedbatch_refresh_token';
const USER_KEY = 'feedbatch_user';
const RBAC_KEY = 'feedbatch_rbac';
const EXPIRES_AT_KEY = 'feedbatch_expires_at';

/** Margem (ms) antes da expiração para acionar o refresh proativo. */
const TOKEN_EXPIRY_LEEWAY_MS = 30_000;

const initialRbacState = {
  accountId: null as string | null,
  memberId: null as string | null,
  role: null as Role | null,
  scopes: {} as MemberScopes,
  globalAccess: false,
};

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  accessToken: null,
  refreshToken: null,
  accessTokenExpiresAt: null,
  isAuthenticated: false,
  isLoading: false,
  isInitialized: false,
  error: null,

  // RBAC fields
  accountId: null,
  memberId: null,
  role: null,
  scopes: {},
  globalAccess: false,

  login: async (credentials: LoginRequest) => {
    set({ isLoading: true, error: null });
    try {
      const response = await authApi.login(credentials);
      handleAuthSuccess(response, set);
    } catch (error: any) {
      console.log('Login error details:', {
        response: error.response,
        data: error.response?.data,
        message: error.message
      });
      
      const errorMessage = error.response?.data?.error 
        || error.response?.data?.message 
        || error.message 
        || 'Falha ao fazer login';
      
      set({ 
        error: errorMessage,
        isLoading: false 
      });
      throw error;
    }
  },

  register: async (data: RegisterRequest) => {
    set({ isLoading: true, error: null });
    try {
      const response = await authApi.register(data);
      handleAuthSuccess(response, set);
    } catch (error: any) {
      const errorMessage = error.response?.data?.error 
        || error.response?.data?.message 
        || error.message 
        || 'Falha ao registrar';
      
      set({ 
        error: errorMessage,
        isLoading: false 
      });
      throw error;
    }
  },

  loginWithGoogle: async (data: GoogleAuthRequest) => {
    set({ isLoading: true, error: null });
    try {
      const response = await authApi.loginWithGoogle(data);
      handleAuthSuccess(response, set);
    } catch (error: any) {
      const errorMessage = error.response?.data?.error 
        || error.response?.data?.message 
        || error.message 
        || 'Falha ao fazer login com Google';
      
      set({ 
        error: errorMessage,
        isLoading: false 
      });
      throw error;
    }
  },

  logout: async () => {
    set({ isLoading: true, error: null });
    try {
      await authApi.logout();
    } catch (error) {
      // Continue with logout even if API call fails
      console.error('Logout API call failed:', error);
    } finally {
      clearAuthData(set);
    }
  },

  refreshAccessToken: async () => {
    const { refreshToken } = get();
    
    if (!refreshToken) {
      clearAuthData(set);
      throw new Error('No refresh token available');
    }

    try {
      const response = await authApi.refreshToken({ refreshToken });
      handleAuthSuccess(response, set);
    } catch (error: any) {
      clearAuthData(set);
      throw error;
    }
  },

  refreshUserData: async () => {
    try {
      const userData = await authApi.getCurrentUser();
      const currentState = get();
      
      // Update user data in state and localStorage
      const updatedUser = { ...currentState.user, ...userData };
      localStorage.setItem(USER_KEY, JSON.stringify(updatedUser));
      
      set({ user: updatedUser });
    } catch (error: any) {
      console.error('Failed to refresh user data:', error);
      throw error;
    }
  },

  refreshUserDataFresh: async () => {
    try {
      const userData = await authApi.getCurrentUserFresh();
      const currentState = get();
      
      // Update user data in state and localStorage
      const updatedUser = { ...currentState.user, ...userData };
      localStorage.setItem(USER_KEY, JSON.stringify(updatedUser));
      
      set({ user: updatedUser });
      
      return updatedUser;
    } catch (error: any) {
      console.error('Failed to refresh user data (fresh):', error);
      throw error;
    }
  },

  clearError: () => {
    set({ error: null });
  },

  handleAuthSuccessFromResponse: (response: AuthResponse) => {
    handleAuthSuccess(response, set);
  },

  initializeAuth: () => {
    const accessToken = localStorage.getItem(TOKEN_KEY);
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    const userStr = localStorage.getItem(USER_KEY);
    const rbacStr = localStorage.getItem(RBAC_KEY);
    const expiresAtStr = localStorage.getItem(EXPIRES_AT_KEY);

    if (accessToken && refreshToken && userStr) {
      try {
        const user = JSON.parse(userStr);
        const rbacData = rbacStr ? JSON.parse(rbacStr) : initialRbacState;
        const accessTokenExpiresAt = expiresAtStr ? Number(expiresAtStr) : null;
        set({
          user,
          accessToken,
          refreshToken,
          accessTokenExpiresAt: Number.isFinite(accessTokenExpiresAt) ? accessTokenExpiresAt : null,
          isAuthenticated: true,
          isInitialized: true,
          ...rbacData,
        });
      } catch (error) {
        console.error('Failed to parse stored user data:', error);
        clearAuthData(set);
        set({ isInitialized: true });
      }
    } else {
      // Não há dados de autenticação, mas a inicialização está completa
      set({ isInitialized: true });
    }
  },

  isAccessTokenExpiring: () => {
    const { accessToken, accessTokenExpiresAt } = get();
    if (!accessToken) {
      return false;
    }
    // Sem informação de expiração conhecida: não força refresh proativo
    // (o interceptor de response continua sendo a rede de segurança).
    if (!accessTokenExpiresAt) {
      return false;
    }
    return Date.now() >= accessTokenExpiresAt - TOKEN_EXPIRY_LEEWAY_MS;
  },
}));

// Helper function to handle successful authentication
function handleAuthSuccess(
  response: AuthResponse,
  set: (partial: Partial<AuthState>) => void
) {
  const { user, accessToken, refreshToken, expiresIn } = response;

  // Extract RBAC data from the user object (backend returns these fields)
  const rbacData = {
    accountId: (user as any).accountId ?? null,
    memberId: (user as any).memberId ?? null,
    role: (user as any).role ?? null,
    scopes: (user as any).scopes ?? {},
    globalAccess: (user as any).globalAccess ?? false,
  };

  // Calcula o instante de expiração a partir de expiresIn (segundos).
  const accessTokenExpiresAt =
    typeof expiresIn === 'number' && expiresIn > 0
      ? Date.now() + expiresIn * 1000
      : null;

  // Store in localStorage
  localStorage.setItem(TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
  localStorage.setItem(RBAC_KEY, JSON.stringify(rbacData));
  if (accessTokenExpiresAt) {
    localStorage.setItem(EXPIRES_AT_KEY, String(accessTokenExpiresAt));
  } else {
    localStorage.removeItem(EXPIRES_AT_KEY);
  }

  // Update state
  set({
    user,
    accessToken,
    refreshToken,
    accessTokenExpiresAt,
    isAuthenticated: true,
    isLoading: false,
    error: null,
    ...rbacData,
  });
}

// Helper function to clear authentication data
function clearAuthData(set: (partial: Partial<AuthState>) => void) {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(RBAC_KEY);
  localStorage.removeItem(EXPIRES_AT_KEY);

  set({
    user: null,
    accessToken: null,
    refreshToken: null,
    accessTokenExpiresAt: null,
    isAuthenticated: false,
    isLoading: false,
    error: null,
    isInitialized: true,
    // Reset RBAC
    accountId: null,
    memberId: null,
    role: null,
    scopes: {},
    globalAccess: false,
  });
}
